import { useState } from "react";
import { useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { WebsiteHeader } from "@/components/layout/website-header";
import { WebsiteFooter } from "@/components/layout/website-footer";
import { SaaSGamaLogo } from "@/components/ui/saasgama-logo";
import {
  ArrowRight,
  Check,
  TrendingUp,
  Shield,
  Users,
  BarChart3,
  Star,
  Zap,
  Target,
  DollarSign,
  Clock,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { SiFacebook, SiInstagram, SiLinkedin, SiYoutube } from "react-icons/si";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const homeFaqs = [
  { q: "What is SaaS Management Software and why do businesses need it?", a: "SaaS Management Software helps businesses manage SaaS apps, subscriptions, licenses, and spending from one platform. SaaSGama improves visibility, controls software usage, and helps optimize SaaS costs." },
  { q: "How does SaaS Management Software help reduce software costs?", a: "SaaS Management Software reduces costs by finding unused subscriptions, tracking usage, managing licenses, and preventing unnecessary renewals. SaaSGama helps businesses optimize software spending." },
  { q: "What is SaaS Subscription Management and how does it work?", a: "SaaS Subscription Management helps businesses track, manage, and optimize software subscriptions. SaaSGama enables better subscription visibility, renewal tracking, and control over SaaS expenses." },
  { q: "How can businesses track and manage SaaS subscriptions in one place?", a: "Businesses can manage SaaS subscriptions through a centralized platform. SaaSGama helps track applications, users, licenses, and spending to simplify SaaS management and improve control." },
  { q: "What are the benefits of using SaaS Spend Management Software?", a: "SaaS Spend Management Software helps control costs, track spending, manage subscriptions, and improve budgeting. SaaSGama helps businesses reduce waste and maximize SaaS investments." },
  { q: "How can SaaS Management Software optimize SaaS spending?", a: "SaaS Management Software optimizes spending by analyzing usage, reducing unused tools, managing licenses, and improving subscription decisions. SaaSGama helps lower SaaS costs effectively." },
  { q: "How does SaaS Management Software help manage licenses and renewals?", a: "SaaS Management Software tracks licenses, monitors usage, and manages renewals efficiently. SaaSGama helps avoid unnecessary renewals, reduce unused licenses, and improve license management." },
  { q: "Can SaaS Management Software identify unused or costly subscriptions?", a: "Yes, SaaS Management Software helps identify unused apps, inactive users, and expensive subscriptions. SaaSGama provides insights to reduce unnecessary spending and optimize SaaS usage." },
  { q: "What features should businesses look for in a SaaS Management Platform?", a: "A SaaS Management Platform should offer subscription tracking, license management, spend visibility, usage insights, and renewal tracking. SaaSGama helps simplify SaaS operations." },
  { q: "How does SaaS Spend Management improve visibility and control over software expenses?", a: "SaaS Spend Management provides insights into software costs, usage, and subscriptions. SaaSGama helps businesses monitor spending, improve control, and make smarter SaaS decisions." },
];

// Import generated images
import heroImage from "@assets/generated_images/SaaS_dashboard_hero_illustration_9775a58d.png";
import teamImage from "@assets/generated_images/Business_team_collaboration_scene_5b0b28d1.png";
import savingsImage from "@assets/generated_images/Cost_savings_optimization_graphic_0d88d6ec.png";
import dashboardImage from "@assets/generated_images/saasgama_overview_dashboard.png";
import successImage from "@assets/generated_images/Corporate_success_team_productivity_8627f822.png";

interface PricingPlan {
  id: string;
  name: string;
  description: string;
  features: string[];
  isPopular: boolean;
  prices: {
    month?: {
      id: string;
      amount: number;
      currency: string;
      interval: string;
    };
    year?: {
      id: string;
      amount: number;
      currency: string;
      interval: string;
    };
  };
}

export default function LandingPage() {
  const [, navigate] = useLocation();
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [activeFeatureCard, setActiveFeatureCard] = useState(0);

  const featureCards = [
    { bg: "from-blue-50 to-cyan-50", border: "border-blue-100", iconBg: "from-blue-500 to-blue-600", icon: <TrendingUp className="w-8 h-8 text-white" />, title: "Smart Cost Optimization", desc: "AI-powered recommendations identify unused licenses and optimization opportunities automatically", stat: "Average savings: 35-45%", statColor: "text-blue-600" },
    { bg: "from-green-50 to-emerald-50", border: "border-green-100", iconBg: "from-green-500 to-green-600", icon: <Shield className="w-8 h-8 text-white" />, title: "Automated Renewal Tracking", desc: "Never miss a renewal again with smart alerts and automated contract management", stat: "100% renewal visibility", statColor: "text-green-600" },
    { bg: "from-purple-50 to-violet-50", border: "border-purple-100", iconBg: "from-purple-500 to-purple-600", icon: <Users className="w-8 h-8 text-white" />, title: "Team License Management", desc: "Effortlessly assign, track, and optimize licenses across your entire organization", stat: "Unlimited team members", statColor: "text-purple-600" },
    { bg: "from-orange-50 to-red-50", border: "border-orange-100", iconBg: "from-orange-500 to-red-500", icon: <BarChart3 className="w-8 h-8 text-white" />, title: "Usage Analytics", desc: "Deep insights into software usage patterns and ROI optimization opportunities", stat: "Real-time monitoring", statColor: "text-orange-600" },
    { bg: "from-indigo-50 to-blue-50", border: "border-indigo-100", iconBg: "from-indigo-500 to-blue-500", icon: <Zap className="w-8 h-8 text-white" />, title: "5-Minute Setup", desc: "Connect your existing tools instantly with our one-click integrations", stat: "50+ integrations", statColor: "text-indigo-600" },
    { bg: "from-teal-50 to-cyan-50", border: "border-teal-100", iconBg: "from-teal-500 to-cyan-500", icon: <Target className="w-8 h-8 text-white" />, title: "Compliance Management", desc: "Stay compliant with automated audit trails and license compliance monitoring", stat: "Enterprise-grade security", statColor: "text-teal-600" },
  ];

  const { data: pricingPlans = [] } = useQuery({
    queryKey: ["/api/pricing-plans"],
  });

  const { data: publicSettings } = useQuery({
    queryKey: ["/api/system-settings"],
    queryFn: async () => {
      const res = await fetch("/api/system-settings");
      if (!res.ok) throw new Error("Failed to load settings");
      return res.json();
    }
  });

  const showFaq = publicSettings?.showLandingFaq !== false;

  const handleGetStarted = (plan?: PricingPlan) => {
    if (plan) {
      navigate(`/register?plan=${plan.id}&billing=${billingCycle}`);
    } else {
      navigate("/register");
    }
  };

  return (
    <div className="landing-page flex flex-col min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Helmet>
        <title>SaaSGama | SaaS Management Software for Businesses</title>
        <meta name="description" content="SaaSGama is a SaaS management platform that helps businesses manage subscriptions, licenses, renewals and software spending efficiently, all in one place." />
        <meta name="keywords" content="SaaS Management Software, SaaS Cost Optimization, License Management Platform, SaaS Spend Management Software, Renewal Tracking" />
        <link rel="canonical" href="https://saasgama.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://saasgama.com/" />
        <meta property="og:site_name" content="SaaSGama" />
        <meta property="og:title" content="SaaSGama | SaaS Management Software for Businesses" />
        <meta property="og:description" content="SaaSGama is a SaaS management platform that helps businesses manage subscriptions, licenses, renewals and software spending efficiently, all in one place." />
        <meta property="og:image" content="https://saasgama.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SaaSGama | SaaS Management Software for Businesses" />
        <meta name="twitter:description" content="SaaSGama is a SaaS management platform that helps businesses manage subscriptions, licenses, renewals and software spending efficiently, all in one place." />
        <meta name="twitter:image" content="https://saasgama.com/og-image.png" />
        <script async src="https://js.stripe.com/v3/pricing-table.js"></script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Organization", "@id": "https://saasgama.com/#organization", "name": "SaaSGama", "url": "https://saasgama.com/", "description": "SaaSGama is an AI-powered SaaS Management Platform that helps businesses manage SaaS applications, subscriptions, licenses, renewals, and software spending.", "brand": { "@type": "Brand", "name": "SaaSGama" } },
            { "@type": "WebSite", "@id": "https://saasgama.com/#website", "url": "https://saasgama.com/", "name": "SaaSGama", "publisher": { "@id": "https://saasgama.com/#organization" } },
            { "@type": "SoftwareApplication", "@id": "https://saasgama.com/#software", "name": "SaaSGama", "applicationCategory": "BusinessApplication", "operatingSystem": "Web", "url": "https://saasgama.com/", "description": "SaaSGama helps businesses track SaaS subscriptions, optimize software costs, manage licenses, monitor usage, control renewals, and improve SaaS spend visibility.", "creator": { "@id": "https://saasgama.com/#organization" }, "featureList": ["SaaS Management", "SaaS Subscription Management", "SaaS Spend Management", "Software License Management", "Renewal Tracking", "Usage Analytics", "SaaS Cost Optimization", "Application Discovery", "License Optimization"] },
            { "@type": "FAQPage", "@id": "https://saasgama.com/#faq", "mainEntity": homeFaqs.map(faq => ({ "@type": "Question", "name": faq.q, "acceptedAnswer": { "@type": "Answer", "text": faq.a } })) }
          ]
        })}</script>
      </Helmet>

      <WebsiteHeader />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white pt-[6.5rem] md:pt-32 pb-20 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-indigo-500/20 rounded-full blur-3xl"></div>

        <div className="relative container mx-auto px-6 pt-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Left Column - Hero Content */}
              <div className="space-y-5 md:space-y-8">
                {/* Trust Badge */}
                <div className="flex items-center space-x-3">
                  <div className="flex -space-x-2">
                    <div className="w-8 h-8 bg-blue-500 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold">
                      A
                    </div>
                    <div className="w-8 h-8 bg-green-500 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold">
                      B
                    </div>
                    <div className="w-8 h-8 bg-purple-500 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold">
                      C
                    </div>
                  </div>
                  <span className="text-blue-200 text-sm font-medium">
                    Trusted by 500+ companies
                  </span>
                </div>

                {/* Main Headline */}
                <div className="space-y-3 md:space-y-6">
                  <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold leading-snug md:leading-tight">
                    SaaS Management{" "}
                    <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                      Software
                    </span>
                  </h1>
                  <p className="text-base md:text-xl text-gray-300 leading-relaxed max-w-2xl">
                    Stop overspending on unused software. SaaSGama automatically
                    tracks, optimizes, and manages your entire software
                    portfolio in one intelligent dashboard.
                  </p>
                </div>

                {/* Key Benefits */}
                <div className="flex flex-wrap gap-3 md:gap-6 text-sm">
                  <div className="flex items-center space-x-2">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-gray-200">Save $50K+ annually</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-gray-200">5-minute setup</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-gray-200">
                      Start managing licenses today
                    </span>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 md:py-4 text-base md:text-lg font-semibold rounded-xl shadow-2xl hover:shadow-blue-500/25 transition-all transform hover:scale-105"
                    asChild
                  >
                    <Link href="/register">
                      Get Your Trial Today
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                  </Button>
                </div>

                {/* Social Proof */}
                <div className="text-sm text-gray-400 pt-2">
                  <span>
                    Join companies like Slack, Notion, and 500+ others saving
                    millions
                  </span>
                </div>
              </div>

              {/* Right Column - Hero Visual */}
              <div className="relative lg:pl-8">
                <div className="relative">
                  {/* Hero Image */}
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src={heroImage}
                      alt="SaaSGama SaaS Management Dashboard"
                      className="w-full h-auto max-w-2xl rounded-2xl"
                    />
                    {/* Overlay with floating elements */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
                  </div>

                  {/* Floating Achievement Cards */}
                  <div className="hidden sm:block absolute -top-4 -right-4 bg-green-500 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg animate-pulse">
                    <DollarSign className="w-4 h-4 inline mr-1" />
                    Live Savings
                  </div>
                  <div className="hidden sm:block absolute -bottom-4 -left-4 bg-blue-500 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg">
                    <BarChart3 className="w-4 h-4 inline mr-1" />
                    Real-time Analytics
                  </div>
                  <div className="hidden sm:block absolute top-1/2 -right-8 bg-purple-500 text-white text-xs font-bold px-3 py-2 rounded-full shadow-lg transform -translate-y-1/2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Auto-Optimization
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              Why Teams Choose SaaSGama
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cut through software chaos with intelligent automation, real-time
              insights, and effortless license management
            </p>
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <div
              className={`bg-gradient-to-br ${featureCards[activeFeatureCard].bg} p-6 rounded-2xl border ${featureCards[activeFeatureCard].border} shadow-lg`}
              onTouchStart={(e) => { (e.currentTarget as any)._touchX = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                const dx = e.changedTouches[0].clientX - (e.currentTarget as any)._touchX;
                if (dx < -50) setActiveFeatureCard((activeFeatureCard + 1) % featureCards.length);
                else if (dx > 50) setActiveFeatureCard((activeFeatureCard - 1 + featureCards.length) % featureCards.length);
              }}
            >
              <div className={`w-14 h-14 bg-gradient-to-r ${featureCards[activeFeatureCard].iconBg} rounded-2xl flex items-center justify-center mb-4`}>
                {featureCards[activeFeatureCard].icon}
              </div>
              <h3 className="text-lg font-bold mb-2 text-gray-800">{featureCards[activeFeatureCard].title}</h3>
              <p className="text-gray-600 text-sm mb-3">{featureCards[activeFeatureCard].desc}</p>
              <div className={`text-sm font-semibold ${featureCards[activeFeatureCard].statColor}`}>{featureCards[activeFeatureCard].stat}</div>
            </div>
            <div className="flex justify-center gap-2 mt-4">
              {featureCards.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveFeatureCard(i)}
                  className={`h-2 rounded-full transition-all ${i === activeFeatureCard ? "bg-blue-600 w-4" : "bg-gray-300 w-2"}`}
                />
              ))}
            </div>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 max-w-7xl mx-auto">
            {featureCards.map((card, i) => (
              <div key={i} className={`group bg-gradient-to-br ${card.bg} p-4 md:p-8 rounded-2xl border ${card.border} hover:shadow-xl transition-all hover:-translate-y-1`}>
                <div className={`w-16 h-16 bg-gradient-to-r ${card.iconBg} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  {card.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-800">{card.title}</h3>
                <p className="text-gray-600 mb-4">{card.desc}</p>
                <div className={`text-sm font-semibold ${card.statColor}`}>{card.stat}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Savings Showcase Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Left Column - Cost Savings Image */}
              <div className="relative">
                <img
                  src={savingsImage}
                  alt="Cost Optimization and Savings Analytics"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
                <div className="hidden sm:block absolute -bottom-6 -right-6 bg-green-600 text-white px-6 py-3 rounded-xl shadow-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold">$120K+</div>
                    <div className="text-sm">Average Annual Savings</div>
                  </div>
                </div>
              </div>

              {/* Right Column - Cost Benefits */}
              <div className="space-y-8">
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-4xl font-bold text-gray-800">
                    Turn your software spend into{" "}
                    <span className="text-green-600">
                      competitive advantage
                    </span>
                  </h2>
                  <p className="text-base md:text-xl text-gray-600">
                    Stop depleting money on unused licenses. Our AI-powered
                    optimization engine identifies savings opportunities in
                    real-time.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-lg border border-green-100">
                    <div className="text-xl md:text-3xl font-bold text-green-600 mb-2">
                      40%
                    </div>
                    <div className="text-gray-700 font-medium">
                      Average cost reduction
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-lg border border-blue-100">
                    <div className="text-xl md:text-3xl font-bold text-blue-600 mb-2">
                      90 days
                    </div>
                    <div className="text-gray-700 font-medium">
                      Time to full ROI
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-lg border border-purple-100">
                    <div className="text-xl md:text-3xl font-bold text-purple-600 mb-2">
                      24/7
                    </div>
                    <div className="text-gray-700 font-medium">
                      Automated monitoring
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-lg border border-orange-100">
                    <div className="text-xl md:text-3xl font-bold text-orange-600 mb-2">
                      500+
                    </div>
                    <div className="text-gray-700 font-medium">
                      Companies trust us
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SaaSGama Dashboard — Cost Savings In Action */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-blue-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-4">See SaaSGama in action</h2>
              <p className="text-base md:text-xl text-gray-300">Your entire SaaS cost picture — in one place, in real time</p>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 max-w-5xl mx-auto">
              <img
                src={dashboardImage}
                alt="SaaSGama CRO Dashboard — Cost Savings Overview"
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl pointer-events-none"></div>
            </div>

            {/* Caption badges */}
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              {[
                { icon: <DollarSign className="w-4 h-4" />, text: "$97,200 total savings opportunity identified", color: "bg-green-600" },
                { icon: <TrendingUp className="w-4 h-4" />, text: "$47,850 additional revenue · +0.92% conversion lift", color: "bg-blue-600" },
                { icon: <BarChart3 className="w-4 h-4" />, text: "Unused licences · renewals · plan optimisations in one view", color: "bg-purple-600" },
              ].map(badge => (
                <div key={badge.text} className={`${badge.color} text-white px-5 py-2 rounded-xl text-sm font-semibold flex items-center gap-2`}>
                  {badge.icon} {badge.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Collaboration Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Left Column - Team Benefits */}
              <div className="space-y-8">
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-4xl font-bold text-gray-800">
                    Built for teams that{" "}
                    <span className="text-blue-600">scale fast</span>
                  </h2>
                  <p className="text-base md:text-xl text-gray-600">
                    From startups to enterprises, SaaSGama grows with your team.
                    Manage licenses, track usage, and optimize costs across
                    departments.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">
                        Department-level insights
                      </h3>
                      <p className="text-gray-600">
                        See which teams are using what and optimize accordingly
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">
                        Automated license assignments
                      </h3>
                      <p className="text-gray-600">
                        New team members get the right tools instantly
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">
                        Role-based access control
                      </h3>
                      <p className="text-gray-600">
                        Keep sensitive tools secure with smart permissions
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Team Image */}
              <div className="relative">
                <img
                  src={teamImage}
                  alt="Professional team collaboration and software management"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
                <div className="absolute -top-4 -left-4 bg-blue-600 text-white px-4 py-2 rounded-xl shadow-lg">
                  <Users className="w-5 h-5 inline mr-2" />
                  Team Management
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-6 pb-2 bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              Simple, Transparent Pricing
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the plan that fits your team size and needs. All plans
              include our core features with a 14-day free trial.
            </p>
          </div>

          {/* No credit card banner */}
          <div className="flex justify-center mb-6">
            <span className="inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 font-bold px-5 py-2 rounded-full text-sm tracking-wide">
              ✓ No credit card required to get started
            </span>
          </div>

          {/* Billing cycle toggle */}
          <div className="flex items-center justify-center mb-8">
            <div className="inline-flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-5 py-2 text-sm font-semibold rounded-md transition-colors ${
                  billingCycle === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-5 py-2 text-sm font-semibold rounded-md transition-colors ${
                  billingCycle === 'yearly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                }`}
              >
                Yearly
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-8 max-w-6xl mx-auto">
            {(pricingPlans as PricingPlan[]).map((plan: PricingPlan) => (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl border-2 p-4 md:p-8 ${
                  plan.isPopular
                    ? "border-blue-500 shadow-2xl md:scale-105"
                    : "border-gray-200 hover:border-blue-300"
                } transition-all hover:shadow-xl`}
              >
                {plan.isPopular && (
                  <div className="absolute -top-3 md:-top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 md:px-6 py-1 md:py-2 rounded-full text-xs md:text-sm font-bold">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="text-center mb-4 md:mb-8">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-1 md:mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base mb-3 md:mb-6">{plan.description}</p>

                  {billingCycle === 'yearly' && plan.prices.year ? (
                    <div className="mb-3 md:mb-6">
                      <div className="text-3xl md:text-4xl font-bold text-gray-800">
                        ${(plan.prices.year.amount / 100).toFixed(0)}
                        <span className="text-base md:text-lg text-gray-600 font-normal">/year</span>
                      </div>
                      {plan.prices.month && (
                        <div className="mt-1 md:mt-2 text-xs md:text-sm text-green-600 font-medium">
                          ${(plan.prices.year.amount / 100 / 12).toFixed(2)}/month — save ${(plan.prices.month.amount / 100 * 12 - plan.prices.year.amount / 100).toFixed(0)}/year
                        </div>
                      )}
                    </div>
                  ) : plan.prices.month ? (
                    <div className="mb-3 md:mb-6">
                      <div className="text-3xl md:text-4xl font-bold text-gray-800">
                        ${(plan.prices.month.amount / 100).toFixed(0)}
                        <span className="text-base md:text-lg text-gray-600 font-normal">/month</span>
                      </div>
                    </div>
                  ) : null}
                </div>

                <ul className="space-y-2 md:space-y-4 mb-5 md:mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="w-4 md:w-5 h-4 md:h-5 text-green-500 mr-2 md:mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm md:text-base">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full py-2 md:py-3 rounded-xl font-semibold transition-all text-sm md:text-base ${
                    plan.isPopular
                      ? "bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-800"
                  }`}
                  onClick={() => handleGetStarted(plan)}
                >
                  Get Started
                </Button>

                {/* Limited Offer coupon — Single plan only */}
                {plan.name.toLowerCase() === 'single' && (
                  <div className="mt-3 rounded-lg overflow-hidden" style={{ border: '1.5px solid #D4AF37' }}>
                    <div className="py-1 px-3 text-center" style={{ background: 'linear-gradient(90deg, #a86200 0%, #D4AF37 50%, #a86200 100%)' }}>
                      <span className="text-white font-bold uppercase tracking-widest" style={{ fontSize: '10px' }}>★ Limited Offer ★</span>
                    </div>
                    <div className="relative py-2 px-3 text-center" style={{ background: 'linear-gradient(135deg, #fffdf0 0%, #fef9c3 100%)' }}>
                      <span className="absolute top-1 left-1.5 text-yellow-300" style={{ fontSize: '8px' }}>✦</span>
                      <span className="absolute top-1 right-1.5 text-yellow-300" style={{ fontSize: '8px' }}>✦</span>
                      <div className="text-xs text-gray-700">Lifetime access for <span className="font-bold text-gray-900">$79</span> once.</div>
                      <div className="mt-1.5 inline-block border border-dashed rounded px-2 py-0.5" style={{ borderColor: '#c78007' }}>
                        <span className="font-bold text-gray-700 tracking-wide" style={{ fontSize: '10px' }}>Code: <span className="font-mono">LIFETIMESG</span></span>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Success Story Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-indigo-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Left Column - Success Image */}
              <div className="relative">
                <img
                  src={successImage}
                  alt="Corporate success and growth with SaaSGama"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
                <div className="absolute -top-4 -right-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-xl shadow-lg">
                  <div className="text-center">
                    <div className="text-xl font-bold">500+</div>
                    <div className="text-sm">Success Stories</div>
                  </div>
                </div>
              </div>

              {/* Right Column - Success Metrics */}
              <div className="space-y-8">
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-4xl font-bold text-gray-800">
                    Your success story{" "}
                    <span className="text-purple-600">starts here</span>
                  </h2>
                  <p className="text-base md:text-xl text-gray-600">
                    Companies using SaaSGama see immediate impact. From startups
                    to Fortune 500, we deliver results.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-4 md:p-6 rounded-xl shadow-lg border border-purple-100">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
                      </div>
                      <div>
                        <div className="text-xl md:text-2xl font-bold text-gray-800">$2.3M</div>
                        <div className="text-gray-600 text-xs md:text-sm">Average annual savings per company</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4 md:p-6 rounded-xl shadow-lg border border-blue-100">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-xl md:text-2xl font-bold text-gray-800">72 hours</div>
                        <div className="text-gray-600 text-xs md:text-sm">Average time to first savings identified</div>
                      </div>
                    </div>
                  </div>

                  <div className="col-span-2 bg-white p-4 md:p-6 rounded-xl shadow-lg border border-purple-100 max-w-[calc(50%-0.5rem)] mx-auto w-full">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Star className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
                      </div>
                      <div>
                        <div className="text-xl md:text-2xl font-bold text-gray-800">4.9/5</div>
                        <div className="text-gray-600 text-xs md:text-sm">Customer satisfaction rating</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {showFaq && (
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-2xl md:text-4xl font-bold mb-6 text-gray-800">Frequently Asked Questions</h2>
                <p className="text-base md:text-xl text-gray-600">
                  Everything you need to know about SaaS management software
                </p>
              </div>

              <Accordion type="single" collapsible className="w-full space-y-4">
                {homeFaqs.map((faq, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border border-gray-200 rounded-lg px-6 bg-white shadow-sm hover:shadow-md transition-shadow">
                    <AccordionTrigger className="text-left text-lg font-semibold text-gray-800 hover:text-blue-600">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pt-2">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-6">
              Ready to optimize your SaaS spending?
            </h2>
            <p className="text-base md:text-xl mb-8 text-blue-100">
              Join 500+ companies saving millions with intelligent license
              management
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-xl shadow-lg transition-all"
                asChild
              >
                <Link href="/register">
                  Get Your Trial Today
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-2 border-white text-white bg-transparent hover:bg-white hover:text-blue-600 px-8 py-4 text-lg font-semibold rounded-xl transition-all"
                asChild
              >
                <Link href="/login">Sign In</Link>
              </Button>
            </div>

            <div className="mt-8 flex justify-center">
              <div className="inline-flex flex-wrap justify-center items-center gap-2 bg-white/10 backdrop-blur border border-white/20 text-white font-semibold px-4 py-3 rounded-full text-xs sm:text-sm">
                <span>✓ No credit card required</span>
                <span className="text-white/40 hidden sm:inline">|</span>
                <span>✓ Cancel anytime</span>
                <span className="text-white/40 hidden sm:inline">|</span>
                <span>✓ Enterprise support available</span>
              </div>
            </div>

          </div>
        </div>
      </section>

      <WebsiteFooter />

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Complete Your Subscription</DialogTitle>
            <DialogDescription>
              Subscribe to {selectedPlan?.name} plan
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}
